import { Objects } from "./Objects.js";
import { MemoryStorage } from "./MemoryStorage.js";

/**
 * Implements the MemoryStorage in away that the default value is set in the constructor.
 * This simplified version will always return a value (never undefined) and does not have async methods,
 * however, it provides a convenient way to get and set properties of the value in the store.
 *
 * Memory persistent store of JS objects for different contexts:
 *
 * - local: Local storage, persistent across sessions, current available in browser environment only (localStorage).
 * - session: Session storage, for one browser tab only, current available in browser environment only (sessionStorage). Later implementations may support session storage also on the server side as in user session.
 * - global: Singleton cache for the whole application. Uses globalThis in both browser and server environments. Note that server-side this is shared by all users of the application.
 *
 * The objects are stored as JSON strings in the session and local storage (must be JSON serializable).
 * The objects are stored directly as-is in "global" (can be any type).
 */
export class SimpleStorage<T extends Record<string, any> = Record<string, any>> {

  private readonly baseUtil: MemoryStorage;

  /**
   * Creates a new SimpleStorage.
   * @param type The type of store to use. See description for possible values.
   * @param name Unique name of the store.
   * @param defaultValue The default value to return if the value is not found.
   */
  constructor(type: "local" | "session" | "global", readonly name: string, private readonly defaultValue: T) {
    this.baseUtil = new MemoryStorage(type, name);
  }

  /**
   * Gets the value from the store.
   * @returns The value from the store or the default value if the value is not found.
   */
  public get(): T {
    return this.baseUtil.get<T>(this.defaultValue) || this.defaultValue;
  }

  /** Gets a property of the value in the store. */
  public getProp<V>(propertyPath: string): V | undefined {
    return Objects.getProperty(this.get(), propertyPath) as V | undefined;
  }

  /** Sets a property of the value in the store. */
  public setProp(propertyPath: string, value: any): void {
    Objects.setProperty(this.get(), propertyPath, value);
  }

  /**
   * Sets the value in the store.
   * @param value The value to set.
   */
  public set(value: T): void {
    this.baseUtil.set(value);
  }

  /**
   * Clears the value of the store (not the entire store, just value identified by the name).
   */
  public clear(): void {
    this.baseUtil.clear();
  }

}